import React, { useState } from "react";
import { METHODOLOGY } from "@/lib/content";
import {
  Compass,
  Gauge,
  Layers,
  FileCheck,
  RefreshCw,
  Check,
} from "lucide-react";

const ICONS = { Compass, Gauge, Layers, FileCheck, RefreshCw };

const Methodology = () => {
  const [active, setActive] = useState(0);
  const step = METHODOLOGY[active];
  const Icon = ICONS[step.icon] || Compass;

  return (
    <section
      id="metodologia"
      data-testid="methodology-section"
      className="relative bg-[#0B0410] text-[#F9F6F0] py-24 lg:py-32 overflow-hidden border-t border-white/5"
    >
      {/* Ambient corner glows */}
      <div
        className="absolute -top-32 -left-32 w-96 h-96 rounded-full pointer-events-none opacity-50"
        style={{
          background:
            "radial-gradient(circle, rgba(61,21,101,0.32) 0%, rgba(61,21,101,0) 70%)",
        }}
        aria-hidden="true"
      />
      <div
        className="absolute -bottom-40 -right-40 w-[28rem] h-[28rem] rounded-full pointer-events-none opacity-40"
        style={{
          background:
            "radial-gradient(circle, rgba(230,213,184,0.18) 0%, rgba(230,213,184,0) 70%)",
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-16">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#E6D5B8]" />
              <span
                className="text-[11px] tracking-luxury uppercase text-[#E6D5B8] font-medium"
                data-testid="methodology-eyebrow"
              >
                Cómo trabajamos contigo
              </span>
            </div>
            <h2
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.08]"
              data-testid="methodology-headline"
            >
              Metodología{" "}
              <span className="italic text-[#E6D5B8]">EMB</span>:{" "}
              cinco etapas, cero improvisación.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base lg:text-lg text-[#F9F6F0]/70 font-light">
              Un proceso documentado, replicable y auditable. Cada etapa con
              entregables concretos para que sepas exactamente dónde estamos en
              tu plan — y por qué.
            </p>
          </div>
        </div>

        {/* Steps strip — vertical numbers + interactive */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Left: numbered list */}
          <div className="lg:col-span-5">
            <ul className="flex flex-col">
              {METHODOLOGY.map((s, idx) => {
                const isActive = idx === active;
                return (
                  <li key={s.n}>
                    <button
                      data-testid={`methodology-step-${s.n}`}
                      onClick={() => setActive(idx)}
                      className={`group w-full text-left flex items-start gap-5 px-5 py-5 border-l-2 transition-all duration-300 ${
                        isActive
                          ? "border-[#E6D5B8] bg-gradient-to-r from-[#E6D5B8]/[0.06] to-transparent"
                          : "border-white/8 hover:border-[#E6D5B8]/50 hover:bg-white/[0.02]"
                      }`}
                    >
                      <span
                        className={`font-display text-3xl lg:text-4xl leading-none transition-colors duration-300 ${
                          isActive
                            ? "text-[#E6D5B8]"
                            : "text-[#F9F6F0]/25 group-hover:text-[#A397B4]"
                        }`}
                      >
                        {s.n}
                      </span>
                      <span className="flex-1 pt-1">
                        <span
                          className={`block font-display text-xl lg:text-2xl leading-tight transition-colors duration-300 ${
                            isActive ? "text-[#F9F6F0]" : "text-[#F9F6F0]/70"
                          }`}
                        >
                          {s.title}
                        </span>
                        <span
                          className={`block text-sm mt-1.5 font-light transition-colors duration-300 ${
                            isActive
                              ? "text-[#F9F6F0]/75"
                              : "text-[#F9F6F0]/40"
                          }`}
                        >
                          {s.summary}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Right: active step detail */}
          <div className="lg:col-span-7">
            <div
              key={step.n}
              data-testid="methodology-step-detail"
              className="relative bg-[#150A1F] border border-[#2A0A4A] rounded-sm p-8 lg:p-12 overflow-hidden"
              style={{
                animation: "stepFade 0.45s cubic-bezier(0.22,1,0.36,1) both",
              }}
            >
              {/* Decorative hex top-right */}
              <svg
                viewBox="0 0 200 230"
                className="absolute -top-8 -right-8 w-44 h-44 opacity-[0.07] pointer-events-none"
                aria-hidden="true"
              >
                <path
                  d="M100 5 L195 60 L195 170 L100 225 L5 170 L5 60 Z"
                  fill="none"
                  stroke="#E6D5B8"
                  strokeWidth="1.2"
                />
              </svg>

              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 flex items-center justify-center bg-[#3D1565]/25 border border-[#3D1565] rounded-sm">
                  <Icon size={22} className="text-[#E6D5B8]" strokeWidth={1.5} />
                </div>
                <div className="text-[11px] tracking-luxury uppercase text-[#E6D5B8] font-medium">
                  Etapa {step.n}
                </div>
              </div>

              <h3 className="font-display text-3xl lg:text-4xl font-light leading-tight mb-5">
                {step.title}
              </h3>

              <p className="text-base text-[#F9F6F0]/75 leading-relaxed font-light">
                {step.detail}
              </p>

              <div className="mt-10 pt-7 border-t border-white/10">
                <div className="text-[10px] tracking-luxury uppercase text-[#E6D5B8] mb-4">
                  Entregables de esta etapa
                </div>
                <ul className="space-y-3">
                  {step.deliverables.map((d) => (
                    <li
                      key={d}
                      className="text-sm text-[#F9F6F0]/85 flex items-start gap-3"
                    >
                      <Check
                        size={15}
                        className="text-[#E6D5B8] shrink-0 mt-0.5"
                        strokeWidth={2}
                      />
                      <span className="font-light">{d}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <p className="text-sm text-[#F9F6F0]/55 max-w-2xl mx-auto">
            La primera etapa (Diagnóstico Patrimonial) siempre es sin costo y
            sin compromiso.
          </p>
          <div className="mt-7">
            <button
              data-testid="methodology-cta"
              onClick={() => {
                const el = document.getElementById("contacto");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
              className="inline-flex items-center gap-2 bg-[#3D1565] hover:bg-[#4a1c79] text-white text-sm tracking-wider uppercase px-8 py-4 rounded-sm transition-all duration-300 hover:-translate-y-0.5"
            >
              Iniciar diagnóstico
              <span aria-hidden="true">→</span>
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes stepFade {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
};

export default Methodology;
